import React, { useEffect, useMemo, useState } from 'react';
import { ExternalLink, Search } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useLocale } from '../context/LocaleContext';

import {
  FIT_FACT_ITEMS,
  FORZA_DEFAULTS,
  HEALTH_SOURCES_LAST_UPDATED,
  HEALTH_SOURCE_REFERENCES,
} from '../constants/healthSources';

const matchesQuery = (values, query) => {
  if (!query) {
    return true;
  }

  const normalized = query.trim().toLowerCase();
  return values.some((value) => String(value || '').toLowerCase().includes(normalized));
};

const SourceChip = ({ sourceId }) => (
  <a
    href={`#${sourceId}`}
    className="rounded-full border border-cyan-400/20 bg-cyan-400/10 px-2.5 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-cyan-100 transition hover:border-cyan-300/40 hover:bg-cyan-300/15"
  >
    {sourceId}
  </a>
);

const getCountLabel = (count, singularKey, pluralKey, t) =>
  count === 1
    ? t(singularKey, '{count}', { count })
    : t(pluralKey, '{count}', { count });

const HealthSourcesPage = () => {
  const { t } = useLocale();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('fact') || '');
  const factQuery = searchParams.get('fact') || '';

  useEffect(() => {
    setSearch(factQuery);
  }, [factQuery]);

  const filteredSources = useMemo(() => (
    HEALTH_SOURCE_REFERENCES.filter((source) => matchesQuery([
      source.id,
      source.label,
      source.title,
      source.summary,
    ], search))
  ), [search]);

  const filteredDefaults = useMemo(() => (
    FORZA_DEFAULTS.filter((item) => matchesQuery([
      item.id,
      item.title,
      item.currentValue,
      item.summary,
      item.refIds.join(' '),
    ], search))
  ), [search]);

  const filteredFacts = useMemo(() => (
    FIT_FACT_ITEMS.filter((fact) => matchesQuery([
      fact.id,
      fact.text,
      fact.refIds.join(' '),
    ], search))
  ), [search]);

  return (
    <div className="min-h-screen bg-[#0f1115] text-white">
      <div className="mx-auto max-w-6xl px-5 py-10 sm:px-8 lg:px-10">
        <div className="rounded-[2rem] border border-white/10 bg-slate-900/50 p-6 shadow-2xl shadow-black/30 backdrop-blur-xl sm:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.22em] text-cyan-300">
                {t('healthSources.eyebrow', 'Evidence & Disclaimer')}
              </p>
              <h1 className="mt-3 text-4xl font-black tracking-[-0.04em] text-white">
                {t('healthSources.title', 'Health Sources')}
              </h1>
              <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-300">
                {t(
                  'healthSources.intro',
                  'This page lists the public-health references behind the health tips you see in Forza, and explains which numbers inside the app are product defaults rather than personalized medical advice.'
                )}
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-slate-300">
              <span className="font-semibold text-white">{t('healthSources.lastUpdated', 'Last updated:')}</span> {HEALTH_SOURCES_LAST_UPDATED}
            </div>
          </div>

          <div className="mt-6 rounded-[1.4rem] border border-red-400/20 bg-red-500/10 p-5">
            <p className="text-[11px] font-black uppercase tracking-[0.18em] text-red-200">
              {t('healthSources.medicalDisclaimerTitle', 'Medical Disclaimer')}
            </p>
            <p className="mt-3 text-sm leading-7 text-red-50/90">
              {t(
                'healthSources.medicalDisclaimerBody',
                'Forza is a wellness app, not a medical device. Everything in the app - plans, targets, coaching tips, and the fact library - is for general information only and is not a substitute for professional medical advice. Talk to a qualified health professional before starting or changing any exercise, nutrition, or sleep routine, especially if you have a medical condition, injury, or are taking medication.'
              )}
            </p>
          </div>

          <div className="mt-6">
            <label htmlFor="health-sources-search" className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
              {t('healthSources.searchLabel', 'Search Fact IDs, source IDs, or keywords')}
            </label>
            <div className="mt-3 flex items-center gap-3 rounded-[1.3rem] border border-white/10 bg-slate-950/70 px-4 py-3">
              <Search className="h-4 w-4 text-slate-500" />
              <input
                id="health-sources-search"
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('healthSources.searchPlaceholder', 'Try F001, sleep, protein, ACT-CDC...')}
                className="w-full bg-transparent text-sm text-white outline-none placeholder:text-slate-500"
              />
            </div>
          </div>

          <section className="mt-8">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-2xl font-bold tracking-tight text-white">
                {t('healthSources.referencesTitle', 'Public-Health References')}
              </h2>
              <p className="text-sm text-slate-400">
                {getCountLabel(filteredSources.length, 'healthSources.sourceCountOne', 'healthSources.sourceCountOther', t)}
              </p>
            </div>
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              {filteredSources.map((source) => (
                <article
                  id={source.id}
                  key={source.id}
                  className="rounded-[1.4rem] border border-white/10 bg-slate-950/70 p-5 shadow-lg shadow-black/20"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-[11px] font-black uppercase tracking-[0.18em] text-cyan-300">{source.id}</p>
                      <h3 className="mt-2 text-lg font-bold text-white">{source.title}</h3>
                    </div>
                    <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-slate-300">
                      {source.label}
                    </span>
                  </div>
                  <p className="mt-3 text-sm leading-7 text-slate-300">{source.summary}</p>
                  <a
                    href={source.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-cyan-200 hover:text-cyan-100"
                  >
                    {t('healthSources.openSource', 'Open source')}
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </article>
              ))}
            </div>
          </section>

          <section className="mt-10">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold tracking-tight text-white">
                  {t('healthSources.planningDefaultsTitle', 'Forza Planning Defaults')}
                </h2>
                <p className="mt-2 text-sm leading-6 text-slate-400">
                  {t(
                    'healthSources.planningDefaultsBody',
                    'These are the starting values Forza uses when building your plan. They are product defaults, not medical prescriptions.'
                  )}
                </p>
              </div>
              <p className="text-sm text-slate-400">
                {getCountLabel(filteredDefaults.length, 'healthSources.defaultCountOne', 'healthSources.defaultCountOther', t)}
              </p>
            </div>
            <div className="mt-4 grid gap-4">
              {filteredDefaults.map((item) => (
                <article
                  key={item.id}
                  className="rounded-[1.4rem] border border-amber-400/20 bg-amber-500/10 p-5"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <p className="text-[11px] font-black uppercase tracking-[0.18em] text-amber-200">{item.id}</p>
                      <h3 className="mt-2 text-lg font-bold text-white">{item.title}</h3>
                    </div>
                    <div className="rounded-full border border-amber-300/20 bg-amber-300/10 px-3 py-1 text-xs font-black uppercase tracking-[0.18em] text-amber-100">
                      {item.currentValue}
                    </div>
                  </div>
                  <p className="mt-3 text-sm leading-7 text-slate-200">{item.summary}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {item.refIds.map((refId) => <SourceChip key={refId} sourceId={refId} />)}
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="mt-10">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-2xl font-bold tracking-tight text-white">
                {t('healthSources.factLibraryTitle', 'Fact Library & Sources')}
              </h2>
              <p className="text-sm text-slate-400">
                {getCountLabel(filteredFacts.length, 'healthSources.factCountOne', 'healthSources.factCountOther', t)}
              </p>
            </div>
            <div className="mt-4 rounded-[1.6rem] border border-white/10 bg-slate-950/70 p-3 shadow-lg shadow-black/20 sm:p-4">
              <div className="grid gap-3">
                {filteredFacts.map((fact) => (
                  <article
                    key={fact.id}
                    className="rounded-[1.2rem] border border-white/6 bg-white/[0.03] px-4 py-3"
                  >
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0">
                        <p className="text-[11px] font-black uppercase tracking-[0.18em] text-cyan-300">{fact.id}</p>
                        <p className="mt-2 text-sm leading-7 text-slate-100">{fact.text}</p>
                      </div>
                      <div className="flex flex-wrap gap-2 lg:max-w-[40%] lg:justify-end">
                        {fact.refIds.map((refId) => <SourceChip key={`${fact.id}-${refId}`} sourceId={refId} />)}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default HealthSourcesPage;
